import { useMemo, useState } from "react"
import { Transition } from "@headlessui/react"
import { ChevronDown, ChevronUp } from "lucide-react"

import { LinkBtn } from "../buttons"

interface ExpandableObjectViewerProps<T extends object | string> {
  data: T
  isStringified?: boolean
  level?: number
  limit?: number
  limitDepth?: boolean
}

const ExpandableObjectViewer = <T extends object | string>({
  data,
  isStringified = false,
  level = 0,
  limit = 5,
  limitDepth = true
}: ExpandableObjectViewerProps<T>) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const objectData = useMemo(() => {
    if (!isStringified) return data
    try {
      return JSON.parse(data as string)
    } catch {
      return {}
    }
  }, [data, isStringified])

  const entries = Object.entries(objectData)
  const showLimit = level === 0 || limitDepth
  const visibleEntries =
    showLimit && !expanded ? entries.slice(0, limit) : entries
  const hasMore = showLimit && entries.length > limit

  const renderValue = (
    key: string | number,
    value: object[] | object | string
  ) => {
    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        return (
          <div className={`space-y-1 ${level > 0 ? "ml-4" : ""}`}>
            {value.map((item, index) =>
              typeof item === "object" && item !== null ? (
                <ExpandableObjectViewer
                  key={`expandable-object-viewer-${key}-${index}`}
                  data={item}
                  level={level + 1}
                  limitDepth={limitDepth}
                />
              ) : (
                <div
                  key={`expandable-object-viewer-${key}-${index}`}
                  className="ml-4"
                >
                  {String(item)}
                </div>
              )
            )}
          </div>
        )
      }
      return (
        <ExpandableObjectViewer
          data={value}
          level={level + 1}
          limitDepth={limitDepth}
        />
      )
    }
    return <span>{String(value)}</span>
  }

  return (
    <div className={`space-y-1 ${level > 0 ? `ml-${level * 4}` : ""}`}>
      {visibleEntries.map(([key, value]) => (
        <div key={key}>
          <span className="font-medium">{`${key}: `}</span>
          {renderValue(key, value as object[] | object | string)}
        </div>
      ))}
      {hasMore && (
        <Transition
          show
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <LinkBtn
            className={`inline-flex items-center font-medium text-primary-400 hover:text-primary-500 hover:underline ${
              expanded ? "pt-3" : "pt-1"
            }`}
            text="base"
            paddingOff
            onClick={() => setExpanded(prev => !prev)}
          >
            <span className="mr-2">{expanded ? "View less" : "View more"}</span>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </LinkBtn>
        </Transition>
      )}
    </div>
  )
}

export default ExpandableObjectViewer
